import {
  CheckCircleOutlined as SuccessIcon,
  Close as CloseIcon,
  DoneAll as MarkReadIcon,
  ErrorOutlined as ErrorIcon,
  InfoOutlined as InfoIcon,
  OpenInNew as OpenIcon,
  WarningAmberOutlined as WarningIcon,
} from '@mui/icons-material';
import {
  alpha,
  Avatar,
  Box,
  Button,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@pulse/ui';
import type { ElementType } from 'react';

import type { Notification, NotificationVariant } from '../types/notification.types';

export interface NotificationItemProps {
  notification: Notification;
  onMarkRead?: (id: string) => void;
  onDismiss?: (id: string) => void;
}

// Icon + alpha only — the actual color comes from the theme palette at render time (see
// `theme.palette[variant].main` below), so this stays in sync with pulseTheme's brand colors
// instead of a second, hardcoded set of hex values.
const VARIANT_META: Record<NotificationVariant, { Icon: ElementType; bgAlpha: number }> = {
  info: { Icon: InfoIcon, bgAlpha: 0.06 },
  success: { Icon: SuccessIcon, bgAlpha: 0.06 },
  warning: { Icon: WarningIcon, bgAlpha: 0.06 },
  error: { Icon: ErrorIcon, bgAlpha: 0.06 },
};

export function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function NotificationItem({ notification, onMarkRead, onDismiss }: NotificationItemProps) {
  const theme = useTheme();
  const variant = notification.variant ?? 'info';
  const { Icon, bgAlpha } = VARIANT_META[variant];
  const color = theme.palette[variant].main;

  return (
    <ListItem
      alignItems="flex-start"
      sx={{
        px: 2,
        py: 1.25,
        gap: 0,
        bgcolor: notification.read ? 'transparent' : alpha(color, bgAlpha),
        transition: 'background-color 0.2s',
        '&:hover': { bgcolor: 'action.hover' },
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <ListItemAvatar sx={{ minWidth: 44, mt: 0.5 }}>
        {notification.avatarSrc ? (
          <Avatar src={notification.avatarSrc} sx={{ width: 34, height: 34 }} />
        ) : notification.avatarText ? (
          <Avatar
            sx={{ width: 34, height: 34, fontSize: 13, bgcolor: alpha(color, 0.15), color, fontWeight: 600 }}
          >
            {notification.avatarText}
          </Avatar>
        ) : (
          <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(color, 0.12), color }}>
            <Icon sx={{ fontSize: 18 }} />
          </Avatar>
        )}
      </ListItemAvatar>

      <ListItemText
        sx={{ my: 0, mr: 1 }}
        slotProps={{ secondary: { component: 'div' } }}
        primary={
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }} spacing={1}>
            <Typography
              variant="body2"
              noWrap
              sx={{ flex: 1, fontSize: '0.8125rem', fontWeight: notification.read ? 400 : 600 }}
            >
              {notification.title}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, fontSize: '0.7rem' }}>
              {timeAgo(notification.timestamp)}
            </Typography>
          </Stack>
        }
        secondary={
          <Box>
            {/* The message is clamped to 2 lines above, so hover reveals the rest in full. */}
            <Tooltip title={notification.message} placement="top">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.5,
                  fontSize: '0.75rem',
                }}
              >
                {notification.message}
              </Typography>
            </Tooltip>
            {notification.actionLabel && (
              <Button
                size="small"
                variant="outlined"
                onClick={notification.onAction}
                endIcon={<OpenIcon sx={{ fontSize: '13px !important' }} />}
                sx={{
                  mt: 0.75,
                  px: 1.25,
                  py: 0.25,
                  fontSize: '0.7rem',
                  minHeight: 0,
                  color,
                  borderColor: alpha(color, 0.4),
                  '&:hover': { borderColor: color, bgcolor: alpha(color, 0.06) },
                }}
              >
                {notification.actionLabel}
              </Button>
            )}
          </Box>
        }
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0, mt: 0.25 }}>
        {!notification.read && onMarkRead && (
          <Tooltip title="Mark as read" placement="left">
            <IconButton
              size="small"
              onClick={() => onMarkRead(notification.id)}
              sx={{ p: 0.5, color: 'text.disabled', '&:hover': { color: 'success.main' } }}
            >
              <MarkReadIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {onDismiss && (
          <Tooltip title="Dismiss" placement="left">
            <IconButton
              size="small"
              onClick={() => onDismiss(notification.id)}
              sx={{ p: 0.5, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </ListItem>
  );
}
